/**
 * Der Rahmen des Bestell-Drawers - Portierung von `openDrawer` (`index.html:9243-9313`),
 * beschraenkt auf das, was um die Tab-Inhalte herum steht: Ueberlagerung, `aside#drawer`,
 * Breite, Greifleiste, Kopf, Reiterleiste, Rumpf-Huelle, Fussleiste und der Rechte-Durchgang.
 *
 * Die INHALTE der fuenf Reiter (`drawerMainTab`, `drawerTasksTab`, `drawerDocsTab`,
 * `drawerPayTab`, `drawerCalcTab`) sind NICHT Teil dieses Tasks - sie folgen in den Tasks 17-21
 * und werden hier als `children` eingehaengt (siehe `DrawerProps.children`).
 *
 * ================= Der Rechte-Durchgang (index.html:9296-9304) =================
 *
 * Original, woertlich:
 *
 *     d.querySelectorAll('.drawer-body input, .drawer-body select, .drawer-body textarea,
 *                         .drawer-body button').forEach(control => {
 *       const own = control.closest('[data-permission]');
 *       if (own ? hasPermission(own.dataset.permission.split(' ')) : canEditTab) return;
 *       control.disabled = true;
 *       control.title = 'Nur Lesen – für Änderungen fehlt die Rollenberechtigung';
 *     });
 *
 * Drei Saetze stecken darin, alle drei tragend:
 *
 * 1. Der Durchgang greift NUR in `.drawer-body`. Kopf (`.drawer-head`) und Fuss (`.drawer-foot`)
 *    liegen ausserhalb und entscheiden je fuer sich ueber `edit_orders`/`delete_orders`.
 * 2. Ein Bedienelement mit eigenem `data-permission` (oder ein Vorfahr damit - `closest`)
 *    entscheidet fuer sich; alle uebrigen folgen `canEditTab`. So bleibt "Zahlung anstossen"
 *    (`data-permission="request_payment"`, `index.html:8632`) fuer ein Standardkonto bedienbar,
 *    waehrend der Rest des Zahlungs-Tabs fuer dieselbe Rolle nur lesbar ist.
 * 3. `data-permission` wird an LEERZEICHEN geteilt - auch dort genuegt EINES der Rechte.
 *
 * Warum das hier imperativ bleibt und nicht als Kontext an die Bedienelemente geht: der Bestand
 * sichert zu, dass JEDES Bedienelement im Rumpf erfasst wird, ohne dass es davon wissen muss. Ein
 * Kontext verlangte Mitwirkung jedes einzelnen Elements - die Tasks 17-21 muessten ihn ueberall
 * einbauen, und ein vergessener Einbau waere eine stille Rechteluecke. Der Durchgang laeuft in
 * `useLayoutEffect` nach JEDEM Zeichnen, so wie `openDrawer` ihn nach jedem Neuaufbau ausfuehrt.
 *
 * Weil React - anders als der Bestand - die DOM-Knoten zwischen zwei Zeichnungen WIEDERVERWENDET
 * (der Bestand baut `innerHTML` jedes Mal neu), merkt sich der Durchgang, was er gesperrt hat, und
 * nimmt es vor dem naechsten Durchgang zurueck. Ohne diese Ruecknahme bliebe ein Feld gesperrt,
 * nachdem das Recht dazugekommen ist.
 *
 * ERWEITERUNG ohne Vorbild im Original (Feldzustand-Optik, Referenz `feldzustand-spec.md`): der
 * Durchgang setzt seit dieser Portierung zusaetzlich `.feld-gesperrt` und ein permanentes
 * Schloss-Icon je gesperrtem Feld, siehe Kommentar direkt am `useLayoutEffect` unten.
 *
 * ================= Die Breite (index.html:4640, 9216-9218, 9220-9241, 9286) ================
 *
 *     let DRAWER_WIDTH = Number(readLocalValue('hubDrawerWidth', '640')) || 640;
 *     function drawerWidthValue() {
 *       return window.innerWidth <= 700 ? window.innerWidth
 *            : Math.max(480, Math.min(window.innerWidth - 28, DRAWER_WIDTH));
 *     }
 *     function startDrawerResize(event) {
 *       if (event.button !== 0 || window.innerWidth <= 700) return;
 *       event.preventDefault();
 *       event.stopPropagation();
 *       const handle = event.currentTarget;
 *       const drawer = $('#drawer');
 *       handle.classList.add('resizing');
 *       document.body.classList.add('is-resizing-drawer');
 *       const onMove = moveEvent => {
 *         DRAWER_WIDTH = Math.max(480, Math.min(window.innerWidth - 28, window.innerWidth - moveEvent.clientX));
 *         drawer.style.width = `${DRAWER_WIDTH}px`;
 *       };
 *       const onUp = () => {
 *         handle.classList.remove('resizing');
 *         document.body.classList.remove('is-resizing-drawer');
 *         document.removeEventListener('pointermove', onMove);
 *         document.removeEventListener('pointerup', onUp);
 *         try { localStorage.setItem('hubDrawerWidth', String(Math.round(DRAWER_WIDTH))); } catch (_) {}
 *       };
 *       document.addEventListener('pointermove', onMove);
 *       document.addEventListener('pointerup', onUp);
 *     }
 *
 * GEMELDETE ABWEICHUNG (Woertlichkeitsregel - gemeldet, nicht repariert, siehe Task-Bericht):
 * der Bestand liest `hubDrawerWidth` GENAU EINMAL, beim Auswerten des Skripts. Diese Portierung
 * liest den Wert bei jedem Einhaengen des Drawers erneut in dasselbe Modul-Global. Beobachtbar
 * unterscheidet sich das nur, wenn `hubDrawerWidth` zwischen Seitenladen und erstem Oeffnen des
 * Drawers von aussen veraendert wird - das tut im Bestand kein Pfad (geschrieben wird der
 * Schluessel ausschliesslich am Ende des Ziehens, `index.html:9237`, und dabei steht `DRAWER_WIDTH`
 * ohnehin schon auf demselben Wert). Ohne diese Abweichung waere die Breite in einem
 * Komponententest nicht setzbar, weil ein Modul-Global nur beim allerersten Import entsteht.
 *
 * ================= Woertlich uebernommene Eigenheiten des Ziehens (startDrawerResize) =========
 *
 * 1. Nur die linke Maustaste zieht, UND nur oberhalb 700px Fensterbreite - bei jeder anderen
 *    Taste bzw. auf einem schmalen Fenster passiert GAR NICHTS, auch kein `preventDefault`.
 * 2. `preventDefault` UND `stopPropagation`.
 * 3. Die Breite rechnet waehrend des Ziehens gegen die FENSTERKANTE (`innerWidth - clientX`),
 *    nicht inkrementell und nicht gegen den Startwert - anders als beim Spaltenziehen in
 *    `Board/SpaltenBreiten.ts`. Dieselbe Klemmung wie `drawerWidthValue()`: `[480, innerWidth-28]`.
 * 4. Zwei Klassen sind Nebenwirkungen am DOM, die kein Zustand ist: `resizing` am Griff und
 *    `is-resizing-drawer` am `<body>`.
 * 5. Die Zuhoerer haengen am `document`, nicht am Griff - der Zeiger verlaesst dessen 14px sofort.
 * 6. `localStorage` wird erst am ENDE des Ziehens beschrieben (gerundet, in `try`/`catch`).
 * 7. Waehrend des Ziehens schreibt `DRAWER_WIDTH` mit UND die Breite wird zusaetzlich IMPERATIV
 *    am `<aside id="drawer">` gesetzt (per `ref`) - KEIN React-State dafuer. `style={{width:
 *    drawerWidthValue()}}` (unten) liest beim naechsten Zeichnen denselben, mitgeschriebenen Wert.
 * 8. GEMELDET, bewusst NICHT ergaenzt: der Bestand beendet das Ziehen nur ueber `pointerup` - anders
 *    als beim Spaltenziehen (`SpaltenBreiten.ts`, Eigenheit 6), das zusaetzlich auf `pointercancel`
 *    hoert. Diese Portierung uebernimmt das woertlich, obwohl das CSS denselben Zug per Touch
 *    vorsieht (`bestand.css:3214 ff.`, `touch-action: none`): ein Zeigerzug per Touch, den der
 *    Browser mit `pointercancel` statt `pointerup` beendet, laesst `is-resizing-drawer` und beide
 *    `document`-Zuhoerer bis zum naechsten Aushaengen der Komponente stehen. Die Wortlaut-Regel ist
 *    die bindende Vorgabe, und das Abraeumen beim Aushaengen (Punkt unten) begrenzt den Schaden.
 *
 * GEMELDETE ABWEICHUNG (React-spezifische Zutat, die der Bestand nicht braucht): der Bestand baut
 * den Drawer bei jedem Oeffnen per `innerHTML` neu, haengengebliebene Zuhoerer sterben mit dem
 * weggeworfenen Knoten. In React nicht - schliesst der Drawer WAEHREND des Ziehens (Escape,
 * Ueberlagerung, Navigation), blieben `pointermove`/`pointerup` am `document` und
 * `is-resizing-drawer` am `<body>` sonst stehen: der ganze Bildschirm haette danach dauerhaft
 * `cursor: ew-resize` und keine Textauswahl mehr. Deshalb haelt diese Portierung den Abraeumer des
 * laufenden Ziehens zusaetzlich in einem `ref` und fuehrt ihn beim Aushaengen der Komponente aus
 * (`useEffect`-Cleanup).
 */
import { useEffect, useLayoutEffect, useRef } from "react";
import type { ReactNode } from "react";
import { DrawerKopf } from "./DrawerKopf.tsx";
import { TabLeiste } from "./TabLeiste.tsx";
import { darfTabBearbeiten, hatRecht, NUR_LESEN_TITEL } from "./drawerRechte.ts";
import type { DrawerTabId } from "./drawerRechte.ts";
import { darfBestellungOeffnen } from "../../lib/bestellungSichtbar.ts";
import { isComplete } from "../../lib/status.ts";
import type { Order } from "../../lib/types.ts";
import { schreibeDrawerBreite } from "../../state/arbeitsplatz.ts";

// Die Regel wohnt seit dem KI-Zugang (Stufe 1) in `lib/bestellungSichtbar.ts` (der Server rechnet mit
// derselben Fassung); der Name bleibt hier erreichbar, damit kein Aufrufer umziehen muss.
export { darfBestellungOeffnen };

/** `index.html:4676`, woertlich. */
function readLocalValue(key: string, fallback = ""): string {
  try {
    return localStorage.getItem(key) ?? fallback;
  } catch { // still: wie im Bestand (index.html:4676) darf ein blockiertes localStorage (privates Fenster o. ae.) den Drawer nicht verhindern - es gilt dann die Vorgabe
    return fallback;
  }
}

/** `index.html:4640`, woertlich - Modul-Global, damit die Breite die Sitzung ueberlebt. */
let DRAWER_WIDTH = Number(readLocalValue("hubDrawerWidth", "640")) || 640;

/** Siehe Kopfkommentar, Abschnitt "Die Breite": erneutes Lesen beim Einhaengen. */
function drawerBreiteAusSpeicherLesen(): void {
  DRAWER_WIDTH = Number(readLocalValue("hubDrawerWidth", "640")) || 640;
}

/** `index.html:9216-9218`, woertlich. */
function drawerWidthValue(): number {
  return window.innerWidth <= 700
    ? window.innerWidth
    : Math.max(480, Math.min(window.innerWidth - 28, DRAWER_WIDTH));
}

/**
 * `icon(name, className)` (`index.html:4016`) samt der Nachbearbeitung durch `hydrateIcons`
 * (`:4021`) - die gemeinsame Registratur zeichnet das fertige `<svg class="lucide lucide-…">`.
 *
 * Bis `feat/09-spa-ikonen` stand hier eine LOKALE Funktion, die nur den Platzhalter
 * `<i data-lucide="…">` erzeugte. Den hydriert in der SPA niemand (die UMD-Fassung laeuft allein
 * im Baseline-Rundlauf des Playwright-Harness) - es blieb eine leere Flaeche. Siehe `Ikone.tsx`.
 */
import { Ikone } from "../Ikone.tsx";
import { LineageHinweis } from "./LineageHinweis.tsx";
import { abschnittAnspringen, abschnitteAngleichen } from "./Abschnitt.tsx";
import { SAMMELSENDUNG_PFLEGEHINWEIS, sperrendeSammelsendung } from "./sammelsendungSperre.ts";

/** Die Nachbarschaft, aus der die Tab-Inhalte (Tasks 17-21) ihre Bezugsdaten ziehen. */
export interface DrawerKontext {
  lieferanten: readonly unknown[];
  deals: readonly unknown[];
  bestellungen: readonly Order[];
}

/** Siehe Prop `sprung`. */
export interface DrawerSprung {
  nr: number;
  bestellungId: string;
  tab: DrawerTabId;
  abschnitt: string | undefined;
}

export interface DrawerProps {
  bestellung: Order;
  /** Der Rechtesatz des angemeldeten Kontos - `CURRENT_PERMISSIONS` im Bestand. */
  rechte: ReadonlySet<string>;
  tab: DrawerTabId;
  /**
   * `data-sec`-Schluessel des Abschnitts, zu dem der Drawer nach dem Oeffnen springt (aufklappen,
   * scrollen, kurz hervorheben - `Abschnitt.tsx#abschnittAnspringen`). Fehlt der Abschnitt im
   * Reiter, bleibt es beim Oeffnen des Reiters.
   */
  abschnitt?: string | null;
  /**
   * Gezielter Zellklick (App-Zustand, nicht Adresse): Zaehler PLUS das Ziel, fuer das er gilt. Jeder
   * Klick erhoeht `nr` und macht so auch den erneuten Klick auf dasselbe Ziel zu einem neuen Sprung.
   * Der Drawer verbraucht einen neuen Sprung erst, wenn Bestellung, Reiter und Abschnitt der Props
   * zum Ziel passen - die Adresse zieht (Router-Transition) einen Render NACH dem Zaehler nach, und
   * der Zwischenrender darf nicht zum ALTEN Abschnitt springen.
   */
  sprung?: DrawerSprung;
  /** `gotoTab` (`index.html:8377`). */
  onTab: (tab: DrawerTabId) => void;
  /** `closeDrawer` (`index.html:9631`). */
  onSchliessen: () => void;
  kontext: DrawerKontext;
  /** `patch(feld, wert)` (`index.html:9642`) fuer die beiden Kopffelder. */
  onFeld?: (feld: string, wert: string) => void;
  /** `toggleArchive(o.id)` (`index.html:9288-9289`). */
  onArchivUmschalten?: (id: string) => void;
  /** `splitOrder(o.id)` (`index.html:9290`). */
  onTeillieferung?: (id: string) => void;
  /** "Split rückgängig" (Task 7): nur an einer Teillieferung (`splitFrom`) mit JTL-Kennung. */
  onSplitRueckgaengig?: (id: string) => void;
  /**
   * Navigiert zur verlinkten Bestellung im `LineageHinweis` (Task 11/12) - ruft `App.tsx#lineageOeffnen`
   * (bevorzugt `drawerOeffnen`, faellt bei einer NICHT im Store stehenden Bestellung - archiviert oder
   * frisch gesplittet - auf denselben Nachladeweg wie ein Tieflink zurueck, siehe dort).
   */
  onLineageOeffnen?: (orderId: string) => void;
  /** Task 7: oeffnet den Sammelsendungs-Drawer der Sendung, deren geteilte Felder hier gesperrt sind. */
  onSammelsendungOeffnen?: (shipmentId: string) => void;
  /**
   * Erzwingt ein Nachladen von `LineageHinweis` (Endabnahme-Fixrunde, Pflichtreview-Befund 2) - siehe
   * Kopfkommentar `App.tsx#lineageVersion`. Nur durchgereicht, keine eigene Logik hier: `Drawer.tsx`
   * reicht den Zaehler unveraendert an `LineageHinweis` weiter, dessen `useEffect` ihn in die
   * Abhaengigkeitsliste aufnimmt.
   */
  lineageVersion?: number;
  /**
   * `deleteOrder(o.id)` (`index.html:9291`). SEIT dem Auftrag "Löschen-Buttons zusammenlegen"
   * (2026-09-01) der EINE Loeschweg fuer den Drawer-Fuss, egal ob die Bestellung einen JTL-Link
   * traegt oder nicht - bis dahin gab es hier ZWEI getrennte Knoepfe/Props (`onLoeschen` fuer den
   * reinen Hub-Loeschpfad, `onJtlLoeschen` fuer den JTL-Loeschweg samt Grund-Dialog). Der Aufrufer
   * (`App.tsx#loeschenAusloesen`) entscheidet SELBST, welcher der beiden bestehenden Ablaeufe hinter
   * dieser einen `id` steckt (`bestellung.jtlOrderId` gesetzt -> JTL-Loeschweg, sonst -> reiner
   * Hub-Loeschpfad) - der Drawer kennt diese Verzweigung nicht mehr, nur noch, OB der Knopf
   * ueberhaupt erscheint (`zeigeLoeschen` unten).
   */
  onLoeschen?: (id: string) => void;
  /** Der Inhalt des offenen Reiters - Tasks 17-21. */
  children?: ReactNode;
  /** Hub-interne Gruppenmitgliedschaft; steht vor dem jeweiligen Reiterinhalt. */
  bestellgruppenBereich?: ReactNode;
}

/** Was der Rechte-Durchgang gesperrt hat, samt Vorzustand fuer die Ruecknahme. */
interface Sperrvermerk {
  element: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | HTMLButtonElement;
  warGesperrt: boolean;
  warTitel: string;
  /**
   * Der eingefuegte (oder wiederverwendete) `.feld-lock-anchor`-Wrapper samt Schloss-Icon darin -
   * siehe useLayoutEffect unten. NACHBESSERUNG (Auftrag 7 Punkt 2, Icon-Anker vereinheitlicht): bis
   * hierher stand hier das nackte `.feld-lock-icon` selbst; JEDES Icon - JSX-seitig wie imperativ -
   * traegt seither seinen eigenen `.feld-lock-anchor`-Positionierungskontext, dieses Feld haelt
   * deshalb den WRAPPER, nicht mehr das Icon selbst.
   *
   * NACHBESSERUNG (Auftrag 8, strukturell statt Sibling-Element): seither OPTIONAL - nur noch
   * `button` und Checkbox-/Radio-`<input>`s bekommen ueberhaupt einen Anker (siehe
   * `brauchtAnker` unten); `input`/`select`/`textarea` (ausser Checkbox/Radio) bekommen ihr
   * Schloss-Symbol seither als CSS `background-image` DIREKT ueber `.feld-gesperrt`
   * (`styles/neubau/feldzustand.css`) - fuer sie bleibt `anker` `undefined`, kein Element mehr, das
   * spaeter fehlerhaft entfernt werden koennte (macht den in Auftrag 7 Punkt 1 gefixten
   * Absturzpfad fuer genau diese Feldtypen strukturell unmoeglich).
   */
  anker?: HTMLElement;
}

/**
 * Braucht dieser Elementtyp weiterhin das Sibling-Icon (`.feld-lock-anchor`/`.feld-lock-icon`)?
 * Auftrag 8: NUR noch `button` und Checkbox-/Radio-`<input>`s - fuer alle anderen `<input>`s sowie
 * `<select>`/`<textarea>` uebernimmt seither `.feld-gesperrt`s eigenes `background-image`
 * (`styles/neubau/feldzustand.css`) das Icon, ohne zusaetzliches DOM-Element. Checkbox/Radio bleiben
 * ausgenommen: ein Hintergrundbild auf einem winzigen nativen Kontrollkaestchen waere unbrauchbar,
 * genau wie ein `<button>` kein `background-image`-Feld im engeren Sinn ist.
 */
function brauchtAnker(
  control: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | HTMLButtonElement,
): boolean {
  if (control instanceof HTMLButtonElement) return true;
  return control instanceof HTMLInputElement && (control.type === "checkbox" || control.type === "radio");
}

export function Drawer({
  bestellung,
  rechte,
  tab,
  abschnitt,
  sprung,
  onTab,
  onSchliessen,
  kontext,
  onFeld,
  onArchivUmschalten,
  onTeillieferung,
  onSplitRueckgaengig,
  onLineageOeffnen,
  onSammelsendungOeffnen,
  lineageVersion,
  onLoeschen,
  children,
  bestellgruppenBereich,
}: DrawerProps) {
  const rumpf = useRef<HTMLDivElement | null>(null);
  const huelle = useRef<HTMLElement | null>(null);
  const gesperrt = useRef<Sperrvermerk[]>([]);
  const eingehaengt = useRef(false);
  /** Der Abraeumer des GERADE laufenden Ziehens - siehe Kopfkommentar, "Die eine React-spezifische
   *  Zutat". `null`, solange nicht gezogen wird. */
  const ziehAbraeumer = useRef<(() => void) | null>(null);

  // Siehe Kopfkommentar, Abschnitt "Die Breite".
  if (!eingehaengt.current) {
    eingehaengt.current = true;
    drawerBreiteAusSpeicherLesen();
  }

  // React-spezifische Zutat (Kopfkommentar): ein Aushaengen WAEHREND des Ziehens raeumt die
  // `document`-Zuhoerer und die DOM-Klassen auf, statt sie stehen zu lassen.
  useEffect(() => {
    return () => {
      ziehAbraeumer.current?.();
    };
  }, []);

  // Beim Einhaengen gilt ein schon vorhandener Sprung als verbraucht: das Einhaengen selbst springt
  // ueber den `abschnitt` der Adresse (Tieflink, frisch geoeffneter Drawer).
  const verbrauchterSprung = useRef<number | undefined>(sprung?.nr);
  const gesehenerSprung = useRef<number | undefined>(sprung?.nr);

  // Gezielter Sprung (siehe Prop `abschnitt`). Die Kinder haengen ihre Effekte VOR diesem ein - die
  // Abschnitte des Reiters sind beim Lauf registriert. Gelaufen wird bei neuem Ziel, Reiter oder
  // anderer Bestellung oder neuem `sprung.nr` mit passendem Ziel; ein Rendern mit unveraenderten Werten springt nicht noch
  // einmal. Vor jedem Lauf werden die Abschnitte wieder an `SEC_COLLAPSED` angeglichen (auch ohne
  // Abschnitt) - ein frueherer Sprung soll beim Bestell-/Reiterwechsel nichts offen halten.
  useEffect(() => {
    if (sprung && sprung.nr !== verbrauchterSprung.current) {
      const passt =
        sprung.bestellungId === String(bestellung.id) && sprung.tab === tab && sprung.abschnitt === (abschnitt ?? undefined);
      if (passt) {
        verbrauchterSprung.current = sprung.nr;
      } else if (sprung.nr !== gesehenerSprung.current) {
        // Schwebend: der neue Zaehler ist da, die Adresse noch nicht. Nichts tun - auch kein Angleichen.
        gesehenerSprung.current = sprung.nr;
        return;
      }
    }
    abschnitteAngleichen();
    if (abschnitt) abschnittAnspringen(abschnitt);
    // `sprung?.nr` statt `sprung`: das Ziel haengt am Zaehler, eine neue Objektidentitaet allein loest nichts aus.
  }, [abschnitt, tab, bestellung.id, sprung?.nr]);

  /** `startDrawerResize` (`index.html:9220-9241`), woertlich - siehe Kopfkommentar. */
  function startDrawerResize(event: React.PointerEvent<HTMLDivElement>): void {
    // Eigenheit 1: nur die linke Maustaste, und nur oberhalb 700px - sonst gar nichts.
    if (event.button !== 0 || window.innerWidth <= 700) return;
    event.preventDefault();
    event.stopPropagation();
    const handle = event.currentTarget;
    const drawer = huelle.current;
    handle.classList.add("resizing");
    document.body.classList.add("is-resizing-drawer");
    const onMove = (moveEvent: PointerEvent): void => {
      DRAWER_WIDTH = Math.max(
        480,
        Math.min(window.innerWidth - 28, window.innerWidth - moveEvent.clientX),
      );
      // `drawer` ungeschuetzt wie im Bestand (`$('#drawer')` existiert dort immer); die Wache hier
      // ist reine TypeScript-Typisierung des `ref` - `huelle.current` ist waehrend eines laufenden
      // Ziehens stets gesetzt, der Griff ist Kind des `<aside>`.
      if (drawer) drawer.style.width = `${DRAWER_WIDTH}px`;
    };
    const abraeumen = (): void => {
      handle.classList.remove("resizing");
      document.body.classList.remove("is-resizing-drawer");
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
      ziehAbraeumer.current = null;
    };
    const onUp = (): void => {
      abraeumen();
      // Eigenheit 6: erst am ENDE des Ziehens schreiben, gerundet, in try/catch - das kapselt
      // `schreibeDrawerBreite` (`state/arbeitsplatz.ts:186-189`) bereits byteweise identisch, statt
      // hier ein zweites Mal den Schluessel `hubDrawerWidth` hart hinzuschreiben.
      schreibeDrawerBreite(DRAWER_WIDTH);
    };
    ziehAbraeumer.current = abraeumen;
    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onUp);
  }

  const canEditTab = darfTabBearbeiten(rechte, tab);

  // `index.html:9296-9304`. Laeuft nach JEDEM Zeichnen, wie im Bestand nach jedem `openDrawer`.
  //
  // ERWEITERUNG gegenueber dem woertlichen Original (Feldzustand-Optik, Referenz
  // `feldzustand-spec.md`): der Bestand kennt an dieser Stelle nur `disabled`/`title`. Diese
  // Portierung setzt zusaetzlich die Klasse `.feld-gesperrt` (`styles/neubau/feldzustand.css`) und
  // fuegt ein permanentes Schloss-Icon ein - beides greift dadurch fuer JEDES rollenbasiert
  // gesperrte Feld in JEDEM Reiter, ohne Einzeleinbau je Tab-Datei (derselbe Grund, aus dem der
  // ganze Durchgang imperativ bleibt, siehe Kopfkommentar).
  //
  // Der Titel wird NUR gesetzt, wenn das Feld noch KEINEN hat: ein strukturell gesperrtes Feld
  // (z. B. "Lieferant wird bei der Anlage festgelegt...", von der jeweiligen Tab-Datei selbst
  // vergeben) behaelt seinen treffenderen Text - `NUR_LESEN_TITEL` wuerde dort faelschlich
  // suggerieren, mit der richtigen Rolle waere das Feld editierbar.
  useLayoutEffect(() => {
    for (const vermerk of gesperrt.current) {
      vermerk.element.disabled = vermerk.warGesperrt;
      vermerk.element.title = vermerk.warTitel;
      vermerk.element.classList.remove("feld-gesperrt");
      // NACHBESSERUNG (Absturz-Fix, Auftrag 7 Punkt 1): NUR einen Anker entfernen, den der
      // imperative Code SELBST erzeugt hat (`data-feld-lock-quelle="drawer"`, unten beim Erzeugen
      // gesetzt). Ein wiederverwendeter JSX-Anker (von den Tab-Dateien gerendert, z. B.
      // `Transport.tsx`s `{dsv ? <><span className="feld-lock-anchor">...</span><DsvHinweis/></> :
      // null}`, oder - NACHGETRAGEN, Auftrag 9 Punkt 3, hier fehlten sie bislang als Beispiel -
      // `Stammdaten.tsx`s Lieferant-Feld und `VorlagenAuswahl.tsx`s Zahlungsart-Switch, beide
      // dauerhaft statt bedingt gerendert) traegt dieses Merkmal NIE und bleibt hier unangetastet -
      // er verschwindet von selbst korrekt, sobald React ihn aus mittlerweile wieder zutreffenden
      // Props (`dsv` erneut false) selbst entfernt. Vorher riss `vermerk.anker.remove()`
      // unterschiedslos JEDEN wiederverwendeten Knoten aus dem DOM - auch React-verwaltete. React
      // haelt seine Fiber-Referenz auf einen so entfernten Knoten fuer weiterhin gueltig; entfernt
      // React ihn in einem SPAETEREN Commit selbst (weil die JSX-Bedingung dann erst umschlaegt),
      // wirft die native `removeChild`-Implementierung
      // `NotFoundError: The node to be removed is not a child of this node.` - reproduziert und
      // abgesichert in `test/komponenten/Drawer/absturzNotFoundError.test.tsx`.
      //
      // NACHBESSERUNG (Auftrag 8, strukturell statt Sibling-Element): `vermerk.anker` ist seither nur
      // noch fuer `button`/Checkbox/Radio gesetzt (siehe `brauchtAnker` oben) - fuer jedes andere
      // `input`/`select`/`textarea` bleibt es `undefined`, die Ruecknahme hat dort nichts zu
      // entfernen (kein Sibling-Element mehr vorhanden). Das betrifft NUR den IMPERATIVEN Anker
      // dieser Schleife - JSX-seitig rendern `Stammdaten.tsx`/`VorlagenAuswahl.tsx` ihren
      // `.feld-lock-anchor`/`.feld-lock-icon` weiterhin selbst, siehe Absatz oben (Auftrag 9 Punkt 3:
      // eine fruehere Fassung dieses Kommentars behauptete faelschlich, der Sibling-Mechanismus sei
      // insgesamt nur noch fuer `button`/Checkbox/Radio in Gebrauch).
      if (vermerk.anker?.dataset.feldLockQuelle === "drawer") vermerk.anker.remove();
    }
    gesperrt.current = [];

    const wurzel = rumpf.current;
    if (!wurzel) return;
    const bedienelemente = wurzel.querySelectorAll<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | HTMLButtonElement
    >("input, select, textarea, button");
    for (const control of Array.from(bedienelemente)) {
      const own = control.closest<HTMLElement>("[data-permission]");
      if (own ? hatRecht(rechte, (own.dataset.permission || "").split(" ")) : canEditTab) continue;

      control.classList.add("feld-gesperrt");
      // NACHBESSERUNG (Prioritaetskollision, feldzustand-spec.md "Prioritaet bei Kollision"): ein
      // Feld, das JSX-seitig bereits `feld-erwartet-leer` traegt (leer, editierbar, erwartet) UND
      // hier zusaetzlich rollenbasiert gesperrt wird, behielt bislang BEIDE Klassen gleichzeitig -
      // welche optisch gewann, war CSS-Zufall statt der vorgegebenen Regel "gesperrt >
      // erwartet-leer". `feld-gesperrt` MUSS die amber-Klasse deshalb im selben Zug entfernen.
      // Umgekehrt (Recht wieder da) wird sie hier NICHT einfach wieder hinzugefuegt - der
      // imperative Code weiss zu diesem spaeten Zeitpunkt nicht mehr zuverlaessig, ob sie beim
      // Sperren galt (der Feldwert kann sich waehrend der Sperre geaendert haben, z. B. durch
      // einen Datenabgleich). Stattdessen erzwingt `rumpfSchluessel` unten (als `key` an
      // `.drawer-body`) bei jeder Aenderung des Rechtesatzes ein Neu-Montieren des Rumpfs, damit
      // React die korrekte Klasse aus den AKTUELLEN Feld-Props frisch setzt, statt eine womoeglich
      // veraltete Annahme des imperativen Codes zu uebernehmen - dasselbe Prinzip wie bei
      // `warGesperrt`/`warTitel` oben (den zuletzt von REACT gesetzten Zustand wiederherstellen),
      // nur ueber ein Neu-Montieren statt ueber einen gemerkten Wert, weil hier - anders als bei
      // `disabled`/`title` - kein stabiler, wertfreier Ausgangszustand existiert.
      control.classList.remove("feld-erwartet-leer");

      // NACHBESSERUNG (Auftrag 8, strukturell statt Sibling-Element): Anker nur noch fuer die
      // Elementtypen einfuegen, die weiterhin eines brauchen (`button`, Checkbox/Radio - siehe
      // `brauchtAnker` oben). Jedes andere `input`/`select`/`textarea` bekommt sein Schloss-Symbol
      // seither ausschliesslich ueber CSS (`background-image` an `.feld-gesperrt`,
      // `styles/neubau/feldzustand.css`) - kein Element mehr einzufuegen oder wiederzuverwenden.
      let anker: HTMLElement | undefined;
      if (brauchtAnker(control)) {
        // Anker nur neu einfuegen, wenn dort noch keiner sitzt - sonst wiederverwenden. Der obere
        // Ruecknahme-Durchlauf entfernt ihn bei jedem Durchgang bereits wieder (sofern selbst
        // erzeugt); die Pruefung ist die in der Spec geforderte Absicherung gegen Doppel-Icons, falls
        // ein Durchgang die Ruecknahme ausnahmsweise nicht durchlaufen haben sollte.
        //
        // NACHBESSERUNG (Auftrag 7 Punkt 2, Icon-Anker vereinheitlicht): JEDES Icon - JSX-seitig wie
        // hier imperativ - sitzt seither in einem eigenen `.feld-lock-anchor`-Wrapper
        // (`styles/neubau/feldzustand.css`), der seinen Positionierungskontext SELBST mitbringt (kein
        // `position:relative` an einem zufaellig passenden Vorfahren wie `.field` mehr noetig). Die
        // Wiederverwendungspruefung greift deshalb auf den WRAPPER zu (`.feld-lock-anchor`), nicht
        // mehr auf das nackte Icon direkt.
        const naechstes = control.nextElementSibling;
        if (naechstes instanceof HTMLElement && naechstes.classList.contains("feld-lock-anchor")) {
          anker = naechstes;
        } else {
          anker = document.createElement("span");
          anker.className = "feld-lock-anchor";
          // NACHBESSERUNG (Absturz-Fix, Auftrag 7 Punkt 1): eindeutiges Quelle-Merkmal NUR an
          // imperativ SELBST erzeugten Ankern - JSX-Anker der Tab-Dateien tragen es nie. Die
          // Ruecknahme-Schleife oben entfernt beim Zuruecknehmen ausschliesslich Anker mit diesem
          // Merkmal, siehe deren Kommentar.
          anker.dataset.feldLockQuelle = "drawer";
          const icon = document.createElement("span");
          icon.className = "feld-lock-icon";
          icon.setAttribute("aria-hidden", "true");
          icon.dataset.feldLock = "";
          icon.textContent = "🔒";
          anker.appendChild(icon);
          control.insertAdjacentElement("afterend", anker);
        }
      }

      gesperrt.current.push({
        element: control,
        warGesperrt: control.disabled,
        warTitel: control.title,
        anker,
      });
      control.disabled = true;
      if (!control.title || control.hasAttribute("data-rollenhinweis-bei-sperre")) {
        control.title = NUR_LESEN_TITEL;
      }
    }
  });

  const id = bestellung.id as string;
  const sammelsendung = sperrendeSammelsendung(bestellung);
  const darfBestellungBearbeiten = hatRecht(rechte, "edit_orders");
  const darfLoeschen = hatRecht(rechte, "delete_orders");
  // `Order.jtlOrderId` ist ueber die Catch-All-Signatur (`lib/types.ts`, `[feld: string]: unknown`)
  // vom Typ `unknown` - `Number(...)` samt `Number.isSafeInteger`-Wache ist deshalb dasselbe Muster
  // wie `App.tsx#drawerJtlUebertragen`/`#drawerTeillieferungOeffnen`, nicht nur Vorsicht um ihrer
  // selbst willen: ein blosser Wahrheitswert-Test auf `unknown` liesse sich TypeScript-seitig zwar
  // auf "nicht null/undefined" (`{}`) einengen, aber NICHT auf `number`.
  const darfJtlLoeschen = hatRecht(rechte, "delete_jtl_order");
  const jtlOrderId = Number(bestellung.jtlOrderId);
  const hatJtlOrderId = Number.isSafeInteger(jtlOrderId) && jtlOrderId > 0;
  // Sichtbarkeit des EINEN Loeschen-Knopfes (Auftrag "Löschen-Buttons zusammenlegen", 2026-09-01,
  // verbindliche Entscheidung des Auftraggebers, Punkt 2/3): OHNE JTL-Link unveraendert wie bisher
  // (`delete_orders`); MIT JTL-Link NUR mit `delete_jtl_order` - fehlt das Recht, verschwindet der
  // Knopf KOMPLETT (kein abgeschwaechter lokaler Loeschweg, der den JTL-Beleg verwaist zuruecklassen
  // wuerde). `delete_orders` allein reicht fuer eine verknuepfte Bestellung NICHT mehr aus - vor
  // diesem Auftrag zeigte `darfLoeschen` allein bereits einen (rein lokalen) Loeschknopf, das war
  // GENAU die Luecke, die dieser Auftrag schliesst.
  const zeigeLoeschen = hatJtlOrderId ? darfJtlLoeschen : darfLoeschen;
  // "Split rückgängig": die Route verlangt edit_orders UND delete_jtl_order (die Teillieferung wird in JTL gelöscht).
  const zeigeSplitRueckgaengig = !!bestellung.splitFrom && hatJtlOrderId && darfJtlLoeschen && darfBestellungBearbeiten;

  /**
   * NACHBESSERUNG (Prioritaetskollision, siehe Kommentar am `useLayoutEffect` oben): `key` an
   * `.drawer-body` unten, aus Reiter UND Rechtesatz - reine Ableitung aus Render-Props, keine
   * eigene Zustandsverwaltung noetig. Aendert sich der INHALT von `rechte` (Recht dazugekommen
   * oder entzogen), montiert React den Rumpf komplett neu; jedes Feld bekommt seine Klasse dann
   * FRISCH aus den aktuellen Props gesetzt, statt eine vom imperativen Rechte-Durchgang zuvor
   * entfernte `feld-erwartet-leer` dauerhaft zu verlieren. `Array.from(rechte).sort()`, damit die
   * Einfuegereihenfolge in das Set keinen Einfluss auf den Schluessel hat.
   */
  const rumpfSchluessel = `${tab}:${Array.from(rechte).sort().join(",")}`;

  if (!darfBestellungOeffnen(bestellung)) return null;

  return (
    <>
      {/* `index.html:3815` - die Ueberlagerung ist ein Geschwister des Drawers, kein Kind.
          `.open` setzt `openDrawer` (`index.html:9311`), `closeDrawer` nimmt es zurueck. */}
      <div id="overlay" className="open" onClick={onSchliessen} />
      <aside
        id="drawer"
        ref={huelle}
        className={`open${tab === "calc" ? " calc-wide" : ""}`}
        style={{ width: `${drawerWidthValue()}px` }}
      >
        <div
          className="drawer-resizer"
          role="separator"
          aria-orientation="vertical"
          aria-label="Breite der Bestellung ändern"
          title="Ziehen, um die Breite zu ändern"
          onPointerDown={startDrawerResize}
        />
        <DrawerKopf
          bestellung={bestellung}
          rechte={rechte}
          onFeld={onFeld}
          onSchliessen={onSchliessen}
        />
        {/*
          Pflichtreview-Befund 2 (zu Commit `5e27997c`): sitzt ABSICHTLICH HIER, ausserhalb von
          `.drawer-body` - dieser Container traegt `key={rumpfSchluessel}` (Reiter + Rechtesatz) und
          montiert bei JEDEM Reiterwechsel komplett neu. Ein `LineageHinweis` DORT hatte bei jedem
          Klick durch main/docs/tasks/pay/calc einen neuen `GET .../lineage`-Abruf ausgeloest UND ein
          offenes Mengen-Popover geschlossen - die Design-Spec ("laedt ... erst beim Oeffnen des
          Drawers") verlangt genau EINEN Abruf pro geoeffneter Bestellung. Hier, direkt neben
          `DrawerKopf`, bleibt die Komponente ueber jeden Reiterwechsel hinweg eingehaengt - `tab`
          steckt in KEINER ihrer beiden Abhaengigkeiten (`orderId` = `bestellung.id`, `lineageVersion`
          - siehe dessen Kopfkommentar in `App.tsx` - Endabnahme-Fixrunde, Pflichtreview-Befund 2),
          ein Reiterwechsel loest also keinen erneuten Ladeversuch aus. `lineageVersion` AENDERT sich
          bewusst unabhaengig vom Reiter (bei einem erfolgreichen Split), genau DAS soll hier ein
          Nachladen ausloesen, ohne dass die Komponente deshalb neu montiert werden muesste.
        */}
        {onLineageOeffnen ? (
          <LineageHinweis
            orderId={String(bestellung.id)}
            onOeffnen={onLineageOeffnen}
            bestellungen={kontext.bestellungen}
            lineageVersion={lineageVersion}
          />
        ) : null}
        {sammelsendung ? (
          <div className="sammelsendung-sperrhinweis">
            <span>{SAMMELSENDUNG_PFLEGEHINWEIS}</span>
            {onSammelsendungOeffnen ? (
              <button
                type="button"
                className="btn-ghost"
                onClick={() => onSammelsendungOeffnen(sammelsendung.shipmentId)}
              >
                {`Sammelsendung ${sammelsendung.dsvShipmentNo} öffnen`}
              </button>
            ) : null}
          </div>
        ) : null}
        <TabLeiste tab={tab} onTab={onTab} />
        <div className="drawer-body" ref={rumpf} key={rumpfSchluessel}>
          {isComplete(bestellung) ? (
            <div className="done-banner icon-label">
              <Ikone name="circle-check-big" />
              Bestellung vollständig – alle Dokumente da &amp; bezahlt
            </div>
          ) : null}
          {bestellgruppenBereich}
          {children}
        </div>
        <div className="drawer-foot">
          {darfBestellungBearbeiten ? (
            <>
              {bestellung.archived ? (
                <button className="btn-sec" onClick={() => onArchivUmschalten?.(id)}>
                  <Ikone name="archive-restore" />
                  Wiederherstellen
                </button>
              ) : (
                <button className="btn-sec" onClick={() => onArchivUmschalten?.(id)}>
                  <Ikone name="archive" />
                  Archivieren
                </button>
              )}
              <button
                className="btn-sec"
                onClick={() => onTeillieferung?.(id)}
                title="Teilmenge aus dieser Bestellung abspalten"
              >
                <Ikone name="split" />
                Teillieferung
              </button>
              {zeigeSplitRueckgaengig ? (
                <button
                  className="btn-sec"
                  onClick={() => onSplitRueckgaengig?.(id)}
                  title="Teillieferung auflösen: Mengen zurück in die Ursprungsbestellung"
                >
                  <Ikone name="split" />
                  Split rückgängig
                </button>
              ) : null}
            </>
          ) : null}
          {/*
            EIN Loeschen-Knopf statt zwei (Auftrag "Löschen-Buttons zusammenlegen", 2026-09-01,
            verbindliche Entscheidung des Auftraggebers) - `zeigeLoeschen` oben entscheidet die
            Sichtbarkeit (kein Knopf mehr fuer eine JTL-verknuepfte Bestellung ohne
            `delete_jtl_order`), `App.tsx#loeschenAusloesen` entscheidet beim Klick, welcher der
            beiden bestehenden Ablaeufe hinter der `id` steckt (reiner Hub-Loeschpfad ohne JTL-Link,
            sonst der bestehende JTL-Loeschweg samt Grund-Dialog UND Aufraeumen der Hub-Zeile) - der
            Drawer selbst kennt diese Verzweigung nicht mehr, nur noch `id`.
          */}
          {zeigeLoeschen ? (
            <button className="btn-danger icon-label" onClick={() => onLoeschen?.(id)}>
              <Ikone name="trash-2" />
              Löschen
            </button>
          ) : null}
        </div>
      </aside>
    </>
  );
}
